import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Checkbox,
  FormField,
  Select,
  Multiselect,
  Box,
  Link,
} from '@amzn/awsui-components-react';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { Contact, EventInput, SignupSummaryFrequency, DayOfWeek } from '@amzn/red-velvet-api';
import './notificationPreferences.scss';
import { EventStepProps } from './Common';
import { publishKatalMetric } from '../../../katalAnalytics';
import { useResolveAliasNames } from '../../../../hooks/useResolveAliasNames';
import { useUser } from '../../../../hooks/user';

export interface NotificationPreferencesProps extends EventStepProps {
  ownerAlias?: string;
  includeExternalRecipients?: boolean;
  /** Highlight this section on arrival from a deep link. Ignored when `isReadOnly`. */
  highlightOnMount?: boolean;
  /** Called after the highlight runs, so it is not repeated. */
  onHighlightShown?: () => void;
  onCoordinatorNotificationInfoClick?: () => void;
}

const HIGHLIGHT_DURATION_MS = 2500;

const ALL_DAYS: DayOfWeek[] = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

export function NotificationPreferencesComponent({
  currentFields,
  updateFieldHandler,
  errors,
  isReadOnly,
  metricPrefix,
  ownerAlias,
  includeExternalRecipients,
  highlightOnMount,
  onHighlightShown,
  onCoordinatorNotificationInfoClick,
}: NotificationPreferencesProps) {
  const { t } = useLocalizedText(['events', 'translation']);
  const { userId: currentUserAlias } = useUser();
  const headingId = useId();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  // Highlights on arrival only, not every time the coordinator comes back to this step.
  const shouldHighlight = useRef(highlightOnMount && !isReadOnly);

  useEffect(() => {
    if (!shouldHighlight.current) {
      return;
    }
    shouldHighlight.current = false;
    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    sectionRef.current?.scrollIntoView?.({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    // preventScroll keeps the section centred instead of jammed against the viewport edge.
    sectionRef.current?.focus?.({ preventScroll: true });
    setIsHighlighted(true);
    onHighlightShown?.();
  }, [onHighlightShown]);

  // Separate effect so a re-render cannot leave the highlight stuck on.
  useEffect(() => {
    if (!isHighlighted) {
      return;
    }
    const ringTimer = setTimeout(() => setIsHighlighted(false), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(ringTimer);
  }, [isHighlighted]);

  // Mirror CoordinatorsPartnersComponent: the live, editable owner lives in
  // currentFields.ownerAlias and changes on ownership transfer. Fall back to the
  // prop/current user so the owner is always resolvable (and thus always a recipient option).
  const effectiveOwnerAlias = currentFields.ownerAlias ?? ownerAlias ?? currentUserAlias;

  const [notifyOnSignup, setNotifyOnSignup] = useState(
    currentFields.notifyCoordinatorOnSignup ?? false
  );
  const [frequency, setFrequency] = useState<SignupSummaryFrequency>(
    currentFields.signupSummaryFrequency ?? SignupSummaryFrequency.NEVER
  );
  const [weeklyDays, setWeeklyDays] = useState<DayOfWeek[]>(
    currentFields.signupSummaryWeeklyDays ?? []
  );
  const [selectedRealTimeRecipients, setSelectedRealTimeRecipients] = useState<Contact[]>(
    currentFields.realTimeNotificationRecipients ?? []
  );
  const [selectedSummaryRecipients, setSelectedSummaryRecipients] = useState<Contact[]>(
    currentFields.signupSummaryRecipients ?? []
  );

  // Resolve names for owner + additional coordinators
  const coordinatorAliases = useMemo(
    () =>
      [effectiveOwnerAlias, ...(currentFields.additionalCoordinators ?? [])].filter(
        Boolean
      ) as string[],
    [effectiveOwnerAlias, currentFields.additionalCoordinators]
  );
  const nameCache = useResolveAliasNames(coordinatorAliases);

  const frequencyOptions = [
    { label: t('notificationPreferences.frequencyNever'), value: SignupSummaryFrequency.NEVER },
    { label: t('notificationPreferences.frequencyDaily'), value: SignupSummaryFrequency.DAILY },
    { label: t('notificationPreferences.frequencyWeekly'), value: SignupSummaryFrequency.WEEKLY },
  ];

  const dayLabels: Record<DayOfWeek, string> = {
    [DayOfWeek.MONDAY]: t('notificationPreferences.monday'),
    [DayOfWeek.TUESDAY]: t('notificationPreferences.tuesday'),
    [DayOfWeek.WEDNESDAY]: t('notificationPreferences.wednesday'),
    [DayOfWeek.THURSDAY]: t('notificationPreferences.thursday'),
    [DayOfWeek.FRIDAY]: t('notificationPreferences.friday'),
    [DayOfWeek.SATURDAY]: t('notificationPreferences.saturday'),
    [DayOfWeek.SUNDAY]: t('notificationPreferences.sunday'),
  };

  // Build deduplicated recipient options from coordinators + contacts
  const recipientOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { label: string; value: string; description?: string }[] = [];

    for (const alias of coordinatorAliases) {
      if (seen.has(alias)) continue;
      seen.add(alias);
      options.push({
        label: nameCache[alias] || `@${alias}`,
        value: alias,
        description:
          alias === effectiveOwnerAlias
            ? t('notificationPreferences.roleOwner')
            : t('notificationPreferences.roleAdditionalCoordinator'),
      });
    }

    currentFields.amazonContacts?.forEach((contact) => {
      const key = contact.alias ?? contact.email ?? '';
      if (!key || seen.has(key)) return;
      // Skip the owner — they're already shown via coordinatorAliases with "Owner" label
      if (key === effectiveOwnerAlias) return;
      seen.add(key);
      options.push({
        label: contact.name ?? key,
        value: key,
        description: t('notificationPreferences.roleAmazonContact'),
      });
    });

    if (includeExternalRecipients) {
      currentFields.externalContacts?.forEach((contact) => {
        const key = contact.email ?? '';
        if (!key || seen.has(key)) return;
        seen.add(key);
        options.push({
          label: contact.name ?? key,
          value: key,
          description: t('notificationPreferences.roleExternalContact'),
        });
      });
    }

    return options;
  }, [
    coordinatorAliases,
    nameCache,
    effectiveOwnerAlias,
    currentFields.amazonContacts,
    currentFields.externalContacts,
    includeExternalRecipients,
    t,
  ]);

  const commit = (overrides: Partial<EventInput>) => {
    updateFieldHandler({ ...currentFields, ...overrides });
  };

  // The logged-in coordinator is the default notification recipient. Resolve their name
  // (empty string until PAPI resolves); it is only auto-selected when the alias is one of
  // the allowed recipient options.
  const currentUserContact: Contact = {
    name: nameCache[currentUserAlias] || '',
    alias: currentUserAlias,
  };

  // Recipients must always be a subset of the visible options. Prune anyone who is no longer
  // an allowed option — coordinators removed from the event, or external contacts when the
  // external feature flag is off (we only persist what the current user is allowed to see).
  // The owner is never force-selected; the default recipient is the logged-in user, added
  // once on enable and freely removable thereafter.
  const optionValues = useMemo(
    () => new Set(recipientOptions.map((o) => o.value)),
    [recipientOptions]
  );
  const hasCurrentUserOption = optionValues.has(currentUserAlias);
  // Add the logged-in user as the default recipient when they're an allowed option and not
  // already present; otherwise leave the list unchanged.
  const withCurrentUserDefault = (list: Contact[]): Contact[] =>
    !hasCurrentUserOption || list.some((r) => r.alias === currentUserAlias)
      ? list
      : [...list, currentUserContact];

  useEffect(() => {
    if (isReadOnly) return;

    const prune = (list: Contact[]): Contact[] =>
      list.filter((r) => optionValues.has(r.alias ?? r.email ?? ''));

    const sameContacts = (a: Contact[], b: Contact[]): boolean =>
      a.length === b.length &&
      a.every((c, i) => (c.alias ?? c.email) === (b[i].alias ?? b[i].email));

    const nextRealTime = prune(selectedRealTimeRecipients);
    const nextSummary = prune(selectedSummaryRecipients);

    const overrides: Partial<EventInput> = {};
    if (!sameContacts(nextRealTime, selectedRealTimeRecipients)) {
      setSelectedRealTimeRecipients(nextRealTime);
      overrides.realTimeNotificationRecipients = nextRealTime;
    }
    if (!sameContacts(nextSummary, selectedSummaryRecipients)) {
      setSelectedSummaryRecipients(nextSummary);
      overrides.signupSummaryRecipients = nextSummary;
    }
    if (Object.keys(overrides).length > 0) {
      commit(overrides);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionValues]);

  const toggleDay = (day: DayOfWeek) => {
    const updated = weeklyDays.includes(day)
      ? weeklyDays.filter((d) => d !== day)
      : [...weeklyDays, day];
    setWeeklyDays(updated);
    commit({ signupSummaryWeeklyDays: updated });
  };

  const renderRecipientSelector = (
    label: string,
    description: string,
    selected: Contact[],
    setSelected: (contacts: Contact[]) => void,
    fieldName: 'realTimeNotificationRecipients' | 'signupSummaryRecipients',
    testId: string,
    errorKey?: string
  ) => (
    <FormField label={label} description={description} errorText={errorKey && errors?.[errorKey]}>
      <SpaceBetween direction="vertical" size="xs">
        <Multiselect
          data-testid={testId}
          selectedOptions={recipientOptions.filter((opt) =>
            selected.some((r) => (r.alias ?? r.email) === opt.value)
          )}
          options={recipientOptions}
          placeholder={t('notificationPreferences.recipientsPlaceholder')}
          disabled={isReadOnly}
          onChange={({ detail }) => {
            const contacts: Contact[] = detail.selectedOptions.map((opt) => {
              const existing =
                currentFields.amazonContacts?.find((c) => (c.alias ?? c.email) === opt.value) ??
                currentFields.externalContacts?.find((c) => (c.alias ?? c.email) === opt.value);
              // Persist the resolved name only; never store the "@alias" display label as the
              // name. Fall back to an empty name (matching CoordinatorsPartnersComponent) when
              // the alias hasn't resolved yet.
              return existing ?? { name: nameCache[opt.value ?? ''] || '', alias: opt.value };
            });
            setSelected(contacts);
            commit({ [fieldName]: contacts });
          }}
        />
        {!includeExternalRecipients && (
          <Box color="text-body-secondary" fontSize="body-s">
            {t('notificationPreferences.externalContactsComingSoon')}
          </Box>
        )}
      </SpaceBetween>
    </FormField>
  );

  return (
    <div
      ref={sectionRef}
      tabIndex={-1}
      role="region"
      aria-labelledby={headingId}
      data-testid="notification-preferences-section"
      style={
        {
          '--notification-preferences-ring-duration': `${HIGHLIGHT_DURATION_MS}ms`,
        } as React.CSSProperties
      }
      className={`notification-preferences${
        isHighlighted ? ' notification-preferences--highlighted' : ''
      }`}
    >
      <Container
        header={
          <Header
            variant="h2"
            info={
              onCoordinatorNotificationInfoClick && (
                <Link
                  data-testid="coordinator-notification-info-help-button"
                  variant="info"
                  data-aci-analytics-name="eventCreation_coordinatorNotificationInfoOpened"
                  onFollow={(e) => {
                    e.preventDefault();
                    if (onCoordinatorNotificationInfoClick) {
                      onCoordinatorNotificationInfoClick();
                    }
                  }}
                >
                  {t('misc.info')}
                </Link>
              )
            }
            description={t('notificationPreferences.sectionDescription')}
          >
            <span id={headingId}>{t('notificationPreferences.sectionHeader')}</span>
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          {/* Real-time section */}
          <SpaceBetween direction="vertical" size="s">
            <Box variant="h3">{t('notificationPreferences.realTimeHeader')}</Box>
            <Checkbox
              data-testid="realtime-notify-checkbox"
              checked={notifyOnSignup}
              disabled={isReadOnly}
              onChange={({ detail }) => {
                setNotifyOnSignup(detail.checked);
                publishKatalMetric(
                  `${metricPrefix}_signupNotificationToggled`,
                  String(detail.checked)
                );
                if (!detail.checked) {
                  setSelectedRealTimeRecipients([]);
                  commit({ notifyCoordinatorOnSignup: false, realTimeNotificationRecipients: [] });
                  return;
                }
                // Enabled — auto-select the logged-in user as the default recipient.
                const updated = withCurrentUserDefault(selectedRealTimeRecipients);
                setSelectedRealTimeRecipients(updated);
                commit({
                  notifyCoordinatorOnSignup: true,
                  realTimeNotificationRecipients: updated,
                });
              }}
            >
              {t('notificationPreferences.signupNotificationLabel')}
            </Checkbox>
            {notifyOnSignup &&
              renderRecipientSelector(
                t('notificationPreferences.realTimeRecipientsLabel'),
                t('notificationPreferences.realTimeRecipientsDescription'),
                selectedRealTimeRecipients,
                setSelectedRealTimeRecipients,
                'realTimeNotificationRecipients',
                'realtime-recipients',
                'realTimeRecipients'
              )}
          </SpaceBetween>

          <hr />

          {/* Summary section */}
          <SpaceBetween direction="vertical" size="s">
            <Box variant="h3">{t('notificationPreferences.summaryHeader')}</Box>
            <FormField
              label={t('notificationPreferences.summaryFrequencyLabel')}
              description={t('notificationPreferences.summaryFrequencyDescription')}
            >
              <Select
                data-testid="summary-frequency-select"
                selectedOption={
                  frequencyOptions.find((o) => o.value === frequency) ?? frequencyOptions[0]
                }
                options={frequencyOptions}
                readOnly={isReadOnly}
                onChange={({ detail }) => {
                  const val =
                    frequencyOptions.find((o) => o.value === detail.selectedOption.value)?.value ??
                    SignupSummaryFrequency.NEVER;
                  setFrequency(val);
                  publishKatalMetric(`${metricPrefix}_summaryFrequencySelected`, val);
                  const clearWeeklyDays = val !== SignupSummaryFrequency.WEEKLY;
                  if (clearWeeklyDays) {
                    setWeeklyDays([]);
                  }
                  if (val === SignupSummaryFrequency.NEVER) {
                    setSelectedSummaryRecipients([]);
                    commit({
                      signupSummaryFrequency: val,
                      signupSummaryRecipients: [],
                      signupSummaryWeeklyDays: [],
                    });
                    return;
                  }
                  const updated = withCurrentUserDefault(selectedSummaryRecipients);
                  setSelectedSummaryRecipients(updated);
                  commit({
                    signupSummaryFrequency: val,
                    signupSummaryRecipients: updated,
                    ...(clearWeeklyDays && { signupSummaryWeeklyDays: [] }),
                  });
                }}
              />
            </FormField>

            {frequency === SignupSummaryFrequency.DAILY && (
              <Box color="text-body-secondary" fontSize="body-s">
                {t('notificationPreferences.dailyDisclaimer')}
              </Box>
            )}

            {frequency === SignupSummaryFrequency.DAILY &&
              renderRecipientSelector(
                t('notificationPreferences.summaryRecipientsLabel'),
                t('notificationPreferences.summaryRecipientsDescription'),
                selectedSummaryRecipients,
                setSelectedSummaryRecipients,
                'signupSummaryRecipients',
                'summary-recipients',
                'summaryRecipients'
              )}

            {frequency === SignupSummaryFrequency.WEEKLY && (
              <>
                <FormField
                  label={t('notificationPreferences.weeklyDaysLabel')}
                  description={t('notificationPreferences.weeklyDaysDescription')}
                  errorText={errors?.summaryWeeklyDays}
                >
                  <SpaceBetween direction="horizontal" size="xs">
                    {ALL_DAYS.map((day) => {
                      const isSelected = weeklyDays.includes(day);
                      return (
                        <span
                          key={day}
                          data-testid={`weekly-day-${day}`}
                          className={[
                            'day-pill',
                            isSelected && 'day-pill--selected',
                            isReadOnly && 'day-pill--disabled',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          role="button"
                          aria-pressed={isSelected}
                          tabIndex={isReadOnly ? -1 : 0}
                          onClick={() => !isReadOnly && toggleDay(day)}
                          onKeyDown={(e) => {
                            if (!isReadOnly && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              toggleDay(day);
                            }
                          }}
                        >
                          {dayLabels[day]}
                        </span>
                      );
                    })}
                  </SpaceBetween>
                </FormField>
                {renderRecipientSelector(
                  t('notificationPreferences.summaryRecipientsLabel'),
                  t('notificationPreferences.summaryRecipientsDescription'),
                  selectedSummaryRecipients,
                  setSelectedSummaryRecipients,
                  'signupSummaryRecipients',
                  'summary-recipients',
                  'summaryRecipients'
                )}
              </>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </div>
  );
}
